/**
 * App icons for the Domain Expert app.
 *
 * The app (the PWA shell) builds its installed-app icons from app_icon_url itself and reads PNG
 * only: 8-bit, non-interlaced, at most 4096 × 4096 pixels and 5 MB. Its icons are full squares,
 * like the letter tile: it crops any other shape to its centre square and rounds the corners
 * itself. Any other file gets the letter icon. These helpers turn any image the browser can
 * decode (JPEG, PNG, WebP, GIF, SVG, AVIF, BMP, ...) into a square PNG the app uses.
 */

/** The side of a converted icon, which is square: the largest icon the app builds. */
export const ICON_PNG_SIZE = 512;
/** The largest PNG, in pixels per side, the app builds icons from. */
export const MAX_ICON_PNG_DIMENSION = 4096;
/**
 * The app's icon corners as a share of the side: the letter tile's 12 px on 64 px. Every icon,
 * an image or the letter tile, is a full square with corners rounded by this share of its side.
 */
export const ICON_CORNER_RADIUS = 12 / 64;
const MAX_ICON_PNG_BYTES = 5 * 1024 * 1024;

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
// Grey, RGB, palette, grey + alpha, RGBA: the 8-bit colour types the app decodes.
const PNG_COLOUR_TYPES = [0, 2, 3, 4, 6];

/** An image that couldn't be made into an icon; the message is written for the admin. */
export class IconConversionError extends Error {
  constructor(message = "This image can't be read by your browser.") {
    super(message);
    this.name = 'IconConversionError';
  }
}

interface PngHeader {
  width: number;
  height: number;
  bitDepth: number;
  colourType: number;
  interlace: number;
}

// The IHDR fields, which must be the first chunk, or null when the bytes aren't a PNG.
const readPngHeader = async (blob: Blob): Promise<PngHeader | null> => {
  const bytes = new Uint8Array(await blob.slice(0, 33).arrayBuffer());
  if (bytes.length < 33 || PNG_SIGNATURE.some((byte, i) => bytes[i] !== byte)) return null;
  const view = new DataView(bytes.buffer);
  if (view.getUint32(8) !== 13 || String.fromCharCode(...bytes.subarray(12, 16)) !== 'IHDR') return null;
  return {
    width: view.getUint32(16),
    height: view.getUint32(20),
    bitDepth: bytes[24],
    colourType: bytes[25],
    interlace: bytes[28],
  };
};

/** Whether the app builds its icons from these bytes as they are: a PNG within its limits. */
export const isUsableIconPng = async (blob: Blob): Promise<boolean> => {
  if (blob.size > MAX_ICON_PNG_BYTES) return false;
  const header = await readPngHeader(blob);
  return (
    header !== null &&
    header.width > 0 &&
    header.height > 0 &&
    header.width <= MAX_ICON_PNG_DIMENSION &&
    header.height <= MAX_ICON_PNG_DIMENSION &&
    header.bitDepth === 8 &&
    header.interlace === 0 &&
    PNG_COLOUR_TYPES.includes(header.colourType)
  );
};

/** "Logo.final.jpeg" -> "Logo.final.png"; a name with nothing before the extension -> "app-icon.png". */
export const iconPngName = (name: string) => `${name.trim().replace(/\.[^./\\]*$/, '') || 'app-icon'}.png`;

// ── Decoding ──────────────────────────────────────────────────────────────────

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

const loadImage = async (blob: Blob): Promise<Decoded | null> => {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
  } catch {
    URL.revokeObjectURL(url);
    return null;
  }
  return { source: img, width: img.naturalWidth, height: img.naturalHeight, release: () => URL.revokeObjectURL(url) };
};

// Raster images. createImageBitmap decodes the whole file or rejects, and applies the EXIF
// rotation. Only a browser without it, or without that option (a TypeError), uses an <img>, which
// also applies the rotation but would draw the missing part of a cut-off file as transparent.
const decodeRaster = async (blob: Blob): Promise<Decoded | null> => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch (err) {
      if (!(err instanceof TypeError)) return null; // can't decode it, or it's cut off
    }
  }
  return loadImage(blob);
};

const SVG_NS = 'http://www.w3.org/2000/svg';
const CSS_PX: Record<string, number> = { px: 1, pt: 4 / 3, pc: 16, in: 96, cm: 96 / 2.54, mm: 96 / 25.4 };

// An absolute SVG length in px ("24", "24px", "18pt"), or null ("100%", "2em", missing).
const svgLength = (value: string | null) => {
  const match = /^\s*(\d*\.?\d+(?:e[+-]?\d+)?)\s*(px|pt|pc|in|cm|mm)?\s*$/i.exec(value ?? '');
  if (!match) return null;
  const px = parseFloat(match[1]) * CSS_PX[(match[2] ?? 'px').toLowerCase()];
  return Number.isFinite(px) && px > 0 ? px : null;
};

// How raster files start (null: any byte). A file that starts like one is never read as an SVG,
// even when its metadata mentions <svg.
const RASTER_SIGNATURES: (number | null)[][] = [
  [0x89, 0x50, 0x4e, 0x47], // PNG
  [0xff, 0xd8, 0xff], // JPEG
  [0x47, 0x49, 0x46, 0x38], // GIF
  [0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50], // WebP: RIFF....WEBP
  [null, null, null, null, 0x66, 0x74, 0x79, 0x70], // AVIF, HEIC: ....ftyp
  [0x42, 0x4d], // BMP
  [0x00, 0x00, 0x01, 0x00], // ICO
  [0x49, 0x49, 0x2a, 0x00], // TIFF, little-endian
  [0x4d, 0x4d, 0x00, 0x2a], // TIFF, big-endian
];

// An SVG: typed as one, or with <svg in its first 4 KB (one sent as application/octet-stream, or
// misnamed), unless it starts like a raster file.
const looksLikeSvg = async (blob: Blob) => {
  const head = new Uint8Array(await blob.slice(0, 4096).arrayBuffer());
  if (RASTER_SIGNATURES.some((signature) => signature.every((byte, i) => byte === null || head[i] === byte))) return false;
  return blob.type === 'image/svg+xml' || /<svg[\s>]/i.test(new TextDecoder().decode(head));
};

// SVG, drawn at the icon size rather than its own: its width and height are set so its shorter
// side is `size`, keeping its aspect ratio (from width/height, else the viewBox; square when it
// has neither), so its centre square is drawn at the icon size.
// Without this an SVG with no size of its own can't be drawn in some browsers, and a small one
// would be drawn small and scaled up blurry.
const decodeSvg = async (blob: Blob, size: number): Promise<Decoded | null> => {
  const doc = new DOMParser().parseFromString(await blob.text(), 'image/svg+xml');
  const svg = doc.documentElement;
  if (svg.namespaceURI !== SVG_NS || svg.localName !== 'svg' || doc.getElementsByTagName('parsererror').length) return null;

  const box = (svg.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number);
  const viewBox = box.length === 4 && box.every(Number.isFinite) && box[2] > 0 && box[3] > 0 ? box : null;
  let width = svgLength(svg.getAttribute('width'));
  let height = svgLength(svg.getAttribute('height'));
  if (viewBox) {
    const ratio = viewBox[2] / viewBox[3];
    if (width && !height) height = width / ratio;
    else if (height && !width) width = height * ratio;
    else if (!width && !height) [width, height] = [viewBox[2], viewBox[3]];
  } else if (width && height) {
    // Without a viewBox the drawing doesn't scale with the new size; this one keeps it in place.
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  if (!width || !height) width = height = size;

  const scale = size / Math.min(width, height);
  svg.setAttribute('width', String(Math.max(1, Math.round(width * scale))));
  svg.setAttribute('height', String(Math.max(1, Math.round(height * scale))));
  const sized = new Blob([new XMLSerializer().serializeToString(doc)], { type: 'image/svg+xml' });
  return loadImage(sized);
};

const decode = async (blob: Blob, size: number): Promise<Decoded> => {
  let decoded: Decoded | null = null;
  try {
    // Something that looked like an SVG but isn't one may still be a raster image.
    if (await looksLikeSvg(blob)) decoded = await decodeSvg(blob, size).catch(() => null);
    if (!decoded) decoded = await decodeRaster(blob);
  } catch {
    decoded = null;
  }
  if (!decoded) throw new IconConversionError();
  if (!decoded.width || !decoded.height) {
    decoded.release();
    throw new IconConversionError();
  }
  return decoded;
};

// ── Drawing ───────────────────────────────────────────────────────────────────

const newCanvas = (width: number, height: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new IconConversionError("Your browser couldn't convert this image.");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  return { canvas, context };
};

// The image's centre square, scaled to `size` × `size`: its shorter side fills the square and the
// longer one is cropped evenly at both ends. The square starts on a whole pixel, so an image
// already `size` across is copied rather than resampled half a pixel off. Large images are halved
// step by step first, so they come out smooth rather than jagged.
const drawCentreSquare = (image: Decoded, size: number) => {
  let side = Math.min(image.width, image.height);
  let x = Math.floor((image.width - side) / 2);
  let y = Math.floor((image.height - side) / 2);

  let source = image.source;
  let previous: HTMLCanvasElement | null = null;
  while (side >= size * 2) {
    const half = Math.ceil(side / 2);
    const step = newCanvas(half, half);
    step.context.drawImage(source, x, y, side, side, 0, 0, half, half);
    if (previous) previous.width = 0; // frees the larger step at once
    previous = step.canvas;
    source = step.canvas;
    side = half;
    x = y = 0;
  }

  const out = newCanvas(size, size);
  out.context.drawImage(source, x, y, side, side, 0, 0, size, size);
  if (previous) previous.width = 0;
  return out.canvas;
};

const exportPng = (canvas: HTMLCanvasElement) =>
  new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob(resolve, 'image/png');
    } catch {
      resolve(null); // a canvas the page may not read
    }
  });

/**
 * Converts an image file into a PNG the app builds its icons from: 8-bit, non-interlaced and
 * square, `size` × `size` (512 by default). The image is scaled so its shorter side is `size`
 * (a small one is scaled up) and cropped to its centre square, as the app shows it: a photo loses
 * its sides, a 4:1 wordmark its ends. Its transparency is kept and its EXIF rotation applied, and
 * animated images give their first frame. The corners aren't rounded here: the app rounds them.
 *
 * Rejects with an IconConversionError when the browser can't decode the whole image ("This image
 * can't be read by your browser.") or can't make such a PNG.
 */
export async function imageToIconPng(image: Blob, size = ICON_PNG_SIZE): Promise<Blob> {
  const decoded = await decode(image, size);
  let png: Blob | null;
  try {
    png = await exportPng(drawCentreSquare(decoded, size));
  } finally {
    decoded.release();
  }
  const header = png && (await readPngHeader(png));
  if (!png || !header || header.width !== size || header.height !== size || !(await isUsableIconPng(png))) {
    throw new IconConversionError("Your browser couldn't convert this image.");
  }
  return png;
}

/** imageToIconPng as a File named after the original ("logo.jpg" -> "logo.png"), ready to upload. */
export async function toIconPngFile(image: Blob, name: string): Promise<File> {
  const png = await imageToIconPng(image);
  return new File([png], iconPngName(name), { type: 'image/png' });
}
